/**
 * The crowd: ~10,000 spectators in ONE draw call.
 *
 * A stadium is only a stadium if the seats are full, and "full" at this scale
 * means five figures of small objects. That rules out `Mesh` per spectator
 * immediately — 10,000 meshes is 10,000 draw calls and a scene graph traversal
 * that costs more than the rendering. It is one `InstancedMesh` over a unit
 * box, coloured per instance, and ANIMATED IN THE VERTEX SHADER.
 *
 * The animation being on the GPU is the load-bearing decision. Bobbing the
 * crowd on the CPU means rewriting 10,000 instance matrices and re-uploading a
 * 640 KB buffer every frame, which is milliseconds of pure waste. Instead each
 * instance carries a phase attribute, the shader reads a clock and an
 * excitement level, and the whole crowd animates for free. `cheer()` therefore
 * costs one uniform write, not a rebuild.
 *
 * Seat layout is a pure, seeded function (`crowdSeats`) that never touches
 * Three.js, so where the seats are — and in particular that none of them are on
 * the pitch, and that the team-coloured blocks are behind the right goals — is
 * asserted numerically rather than by looking at a screenshot.
 */

import {
  BoxGeometry,
  Color,
  InstancedBufferAttribute,
  InstancedMesh,
  Matrix4,
  MeshLambertMaterial,
  Quaternion,
  Vector3,
  type WebGLProgramParametersWithUniforms,
} from 'three';
import { ARENA } from '../physics/constants';
import { TEAM_COLORS_DIM } from './materials';
import { StadiumRng, clamp01 } from './random';
import { bowlPerimeter, bowlPoint, bowlSamples } from './bowl';

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

export interface TierLayout {
  name: string;
  /** Distance outside the arena wall of the front row, uu. */
  frontOffset: number;
  /** Height of the front row above the pitch, uu. */
  frontY: number;
  rows: number;
  /** Horizontal step between rows (the tread), uu. */
  run: number;
  /** Vertical step between rows (the riser), uu. */
  rise: number;
  /** Spacing along a row, uu. Seats are placed at this pitch, jittered. */
  seatSpacing: number;
  /** Vomitory (tunnel) gaps cut into the tier: how many, and how wide in uu. */
  vomitories: number;
  vomitoryWidth: number;
}

/**
 * The two seating decks.
 *
 * The front row of the lower tier sits 360 uu behind the wall and 170 uu above
 * the pitch: high enough to be seen over the LED boards from a chase camera at
 * car height, close enough that it is unmistakably behind the glass rather than
 * floating in the distance.
 */
export const TIERS: readonly TierLayout[] = [
  {
    name: 'lower',
    frontOffset: 360,
    frontY: 170,
    rows: 11,
    run: 132,
    rise: 80,
    seatSpacing: 88,
    vomitories: 14,
    vomitoryWidth: 210,
  },
  {
    name: 'upper',
    frontOffset: 1960,
    frontY: 1500,
    rows: 13,
    run: 146,
    rise: 94,
    seatSpacing: 92,
    vomitories: 18,
    vomitoryWidth: 240,
  },
];

/**
 * Half-depth of each team's block of supporters, measured from the back wall
 * toward halfway. Blue defends -Y in the physics frame, which is +Z here.
 */
export const TEAM_BLOCK_Z = 2600;

export interface CrowdSeat {
  /** THREE frame position of the spectator's base. */
  x: number;
  y: number;
  z: number;
  /** Yaw so the box faces the pitch, radians. */
  yaw: number;
  width: number;
  height: number;
  depth: number;
  /** Packed 0xRRGGBB. */
  color: number;
  /** 0..1 animation phase; the shimmer would be a Mexican wave without it. */
  phase: number;
  /** Which end the seat is behind: 0 blue (+z), 1 orange (-z), null neutral. */
  team: 0 | 1 | null;
}

/**
 * Base clothing colours. Deliberately desaturated and dark — a crowd rendered
 * in saturated primaries reads as confetti, and at this distance the only thing
 * that has to survive is the value variation.
 */
export const CROWD_COLORS: readonly number[] = [
  0x2a2f3a, 0x3a3f4b, 0x4a4033, 0x5a5560, 0x6b6357, 0x8a8478, 0x2f3f4f, 0x53414a,
  0x7a6a5c, 0x3d4a42, 0x9a9086, 0x1f242e,
];

/**
 * Is the perimeter fraction `t` inside one of `count` evenly spaced vomitories?
 *
 * Pure, and separate from the seat loop, because "the tunnels are evenly spaced
 * and the right width" is a thing that can silently become "there are no
 * tunnels" when the width is compared in the wrong units.
 */
export const inVomitory = (
  t: number,
  count: number,
  widthFraction: number
): boolean => {
  if (count <= 0 || widthFraction <= 0) return false;
  const cell = 1 / count;
  const local = ((t % cell) + cell) % cell;
  const d = Math.min(local, cell - local);
  return d < widthFraction / 2;
};

/** Team of a seat by its THREE z, or null if it is not behind a goal. */
export const seatTeam = (z: number): 0 | 1 | null => {
  if (z > ARENA.halfLength - TEAM_BLOCK_Z) return 0;
  if (z < -(ARENA.halfLength - TEAM_BLOCK_Z)) return 1;
  return null;
};

/**
 * Seat colour: a base garment tinted toward the home team's colour with
 * probability rising to certainty right behind the goal. That gradient is what
 * makes the ends read as blocks of colour without every seat being identical.
 */
export const seatColor = (base: number, team: 0 | 1 | null, strength: number): number => {
  if (team === null || strength <= 0) return base;
  const t = clamp01(strength);
  // The DIM variants, not the primaries: 11,000 instances of a saturated
  // primary is a wall of neon, and the ends stop reading as people.
  const tc = TEAM_COLORS_DIM[team];
  const mix = (shift: number, mask: number): number => {
    const a = (base >> shift) & mask;
    const b = (tc >> shift) & mask;
    return Math.round(a + (b - a) * t) & mask;
  };
  return (mix(16, 0xff) << 16) | (mix(8, 0xff) << 8) | mix(0, 0xff);
};

export interface CrowdLayoutOptions {
  seed?: number;
  tiers?: readonly TierLayout[];
  /** Fraction of seats left empty. A perfectly full bowl looks like a texture. */
  emptyFraction?: number;
}

export const CROWD_SEED = 0x5eaf17d;

/**
 * Lay out the whole crowd. Pure and seeded.
 *
 * Seats are placed on the TREAD of each row, one step back from its front edge,
 * so a spectator stands on the step rather than hovering over the riser. Every
 * seat is therefore at least `frontOffset` outside the arena wall by
 * construction — which the tests check rather than assume.
 */
export const crowdSeats = (options: CrowdLayoutOptions = {}): CrowdSeat[] => {
  const rng = new StadiumRng(options.seed ?? CROWD_SEED);
  const tiers = options.tiers ?? TIERS;
  const empty = options.emptyFraction ?? 0.055;
  const out: CrowdSeat[] = [];

  for (const tier of tiers) {
    for (let row = 0; row < tier.rows; row++) {
      // Stand on the middle of the tread, on top of the riser below it.
      const offset = tier.frontOffset + (row + 0.5) * tier.run;
      const y = tier.frontY + (row + 1) * tier.rise;
      const perimeter = bowlPerimeter(offset);
      const count = Math.max(8, Math.round(perimeter / tier.seatSpacing));
      const samples = bowlSamples(count, offset);
      const vomWidth = tier.vomitoryWidth / perimeter;

      for (let i = 0; i < samples.length; i++) {
        const t = i / samples.length;
        if (inVomitory(t, tier.vomitories, vomWidth)) continue;
        if (rng.chance(empty)) continue;

        const p = bowlPoint(offset, samples[i]);
        // Jitter along the row and across the tread so the rows are not a grid.
        const jitterAlong = rng.range(-0.3, 0.3) * tier.seatSpacing;
        const jitterOut = rng.range(-0.22, 0.22) * tier.run;
        // Tangent = normal rotated 90 degrees in the plane.
        const tx = -p.nz;
        const tz = p.nx;
        const x = p.x + tx * jitterAlong + p.nx * jitterOut;
        const z = p.z + tz * jitterAlong + p.nz * jitterOut;

        const team = seatTeam(z);
        // Certain right behind the goal, fading out toward the corners.
        const strength =
          team === null
            ? 0
            : clamp01((Math.abs(z) - (ARENA.halfLength - TEAM_BLOCK_Z)) / 2100) * 0.52;
        const base = CROWD_COLORS[rng.int(CROWD_COLORS.length)];

        const scale = rng.range(0.86, 1.14);
        out.push({
          x,
          y: y + rng.range(-4, 4),
          z,
          // atan2(-nx, -nz) turns a +Z-facing box to look back down the normal.
          yaw: Math.atan2(-p.nx, -p.nz),
          width: 46 * scale,
          height: 72 * scale,
          depth: 32 * scale,
          color: seatColor(base, team, strength),
          phase: rng.next(),
          team,
        });
      }
    }
  }
  return out;
};

// ---------------------------------------------------------------------------
// GPU
// ---------------------------------------------------------------------------

/**
 * Injected into `MeshLambertMaterial`'s vertex shader.
 *
 * Offsets are in the box's LOCAL space, so they are multiplied by the instance
 * scale — a 0.05 idle bob on a 78 uu spectator is ~4 uu, which is the right
 * order for "the stand is never completely still" without turning the crowd
 * into a boiling mess.
 *
 * `instanceMatrix[3][2]` is the instance's world z, which is how the shader
 * knows which end of the ground it is standing at without a second attribute.
 */
const CROWD_VERTEX_HEAD = /* glsl */ `
attribute float aPhase;
uniform float uTime;
uniform float uExcite;
uniform float uExciteSide;
uniform float uIdle;
`;

const CROWD_VERTEX_BODY = /* glsl */ `
{
  float ph = aPhase * 6.2831853;
  // Two incommensurate rates, so the shimmer never visibly loops.
  float idle = sin(uTime * 1.7 + ph) * 0.5 + sin(uTime * 2.63 + ph * 2.3) * 0.5;
  transformed.y += idle * uIdle;

  float zw = instanceMatrix[3][2];
  // The end that scored jumps hardest; everyone else still gets up.
  float side = mix(0.45, 1.0, step(0.0, zw * uExciteSide));
  // abs(sin) is a bounce: up fast, land, up again.
  float jump = abs(sin(uTime * 5.5 + ph)) * uExcite * side;
  transformed.y += jump * 0.85;
  transformed.xz *= 1.0 + jump * 0.06;
}
`;

export interface CrowdVisual {
  mesh: InstancedMesh;
  seats: readonly CrowdSeat[];
  /** Advance the clock and decay any celebration. `dt` in seconds. */
  update(time: number, dt: number): void;
  /** Set the crowd off. `team` is who scored; their end reacts hardest. */
  cheer(team: 0 | 1, strength?: number): void;
  dispose(): void;
}

/** How fast a celebration decays, 1/s. ~2.5 s of visible reaction. */
const CHEER_DECAY = 1.1;

export const buildCrowd = (options: CrowdLayoutOptions = {}): CrowdVisual => {
  const seats = crowdSeats(options);

  const geometry = new BoxGeometry(1, 1, 1);
  // Base on y = 0 so an instance sits ON the step rather than half inside it.
  geometry.translate(0, 0.5, 0);

  const phases = new Float32Array(seats.length);
  for (let i = 0; i < seats.length; i++) phases[i] = seats[i].phase;
  geometry.setAttribute('aPhase', new InstancedBufferAttribute(phases, 1));

  const uniforms = {
    uTime: { value: 0 },
    uExcite: { value: 0 },
    uExciteSide: { value: 1 },
    uIdle: { value: 0.045 },
  };

  // Lambert, not Standard: the crowd is 10,000 instances of matte cloth at
  // 6,000 uu. A full PBR evaluation per fragment buys nothing you can see.
  const material = new MeshLambertMaterial({ color: 0xffffff });
  material.onBeforeCompile = (shader: WebGLProgramParametersWithUniforms) => {
    shader.uniforms.uTime = uniforms.uTime;
    shader.uniforms.uExcite = uniforms.uExcite;
    shader.uniforms.uExciteSide = uniforms.uExciteSide;
    shader.uniforms.uIdle = uniforms.uIdle;
    shader.vertexShader = CROWD_VERTEX_HEAD + shader.vertexShader;
    shader.vertexShader = shader.vertexShader.replace(
      '#include <begin_vertex>',
      `#include <begin_vertex>\n${CROWD_VERTEX_BODY}`
    );
  };

  const mesh = new InstancedMesh(geometry, material, seats.length);
  mesh.name = 'crowd';
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  // The bounding sphere would contain the camera for most of a match, so the
  // frustum test can only ever answer "yes" — at the cost of computing it.
  mesh.frustumCulled = false;

  const matrix = new Matrix4();
  const quaternion = new Quaternion();
  const position = new Vector3();
  const scale = new Vector3();
  const up = new Vector3(0, 1, 0);
  const colour = new Color();

  for (let i = 0; i < seats.length; i++) {
    const s = seats[i];
    position.set(s.x, s.y, s.z);
    quaternion.setFromAxisAngle(up, s.yaw);
    scale.set(s.width, s.height, s.depth);
    matrix.compose(position, quaternion, scale);
    mesh.setMatrixAt(i, matrix);
    colour.setHex(s.color);
    // setHex assumes sRGB by default in three, which is what CROWD_COLORS are.
    mesh.setColorAt(i, colour);
  }
  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;

  return {
    mesh,
    seats,
    update(time: number, dt: number): void {
      uniforms.uTime.value = time;
      if (uniforms.uExcite.value > 0) {
        uniforms.uExcite.value = Math.max(
          0,
          uniforms.uExcite.value * Math.exp(-CHEER_DECAY * Math.max(0, dt))
        );
        if (uniforms.uExcite.value < 1e-3) uniforms.uExcite.value = 0;
      }
    },
    cheer(team: 0 | 1, strength = 1): void {
      // Blue defends -Y in physics, which is +Z here: their fans are at +Z.
      uniforms.uExciteSide.value = team === 0 ? 1 : -1;
      uniforms.uExcite.value = Math.max(uniforms.uExcite.value, clamp01(strength));
    },
    dispose(): void {
      geometry.dispose();
      material.dispose();
      mesh.dispose();
    },
  };
};
